import {faRefresh} from '@fortawesome/pro-solid-svg-icons/faRefresh';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useInfiniteClientActivity} from "@twigsby/client-data";
import {MailFileThumbnailType} from "@twigsby/client-data/lib/Types/MailFileTypes";
import {
    ColourSolid,
    ContentBlock,
    ContentLoader,
    InfiniteLoadTrigger,
    ListItem,
    RichEmptyState,
    useLayout,
    useTranslation
} from "@twigsby/frontend-framework";
import {FormatLocaleRelativeStrict} from "@twigsby/helpers";
import React, {forwardRef, useCallback, useEffect, useState} from "react";
import {useSite} from "../../Providers/SiteInterfaceProvider";
import {ActivityImage} from "../Images/ActivityImage";
import {
    MailItemNoThumbnail,
    MailItemThumbnail,
    MailItemTopUpRequired,
    MailPartTypeEnum
} from "../Mail/MailItemThumbnails";

type ActivityItemType = {
    eventId: number;
    message: string;
    eventDate: Date;
    metadata: ActivityItemMetaType;
    url: string;
}

type ActivityItemMetaType = {
    cost?: number;
    contentThumbnails?: ActivityItemFileType;
    envelopeThumbnails?: ActivityItemFileType;
    topupRequired?: boolean;
    trackingId?: string;
    mailUuid?: string;
    batchId?: number;
    batchIdNo?: string;
    batchThumbnails?: Array<ActivityItemFileType>;
}

type ActivityItemFileType = {
    thumbnails: Array<MailFileThumbnailType>;
    mailPart: number;
    topupRequired: boolean;
}

const ActivityItem = (item: ActivityItemType) => {
    const url: string = item.metadata.mailUuid ? `./mail/inbox/${item.metadata.mailUuid}` : `./mail/forwarding-batches/${item.metadata.batchId}`;

    return (
        <ActivityItemContent {...item} url={(item.metadata.mailUuid || item.metadata.batchId) ? url : `./manage-account/payment/transactions`} />
    )
}

const ActivityItemContent = (item: ActivityItemType) => {
    const { currentLocale } = useSite();
    const { isMedium } = useLayout();

    return (
        <ListItem
            actionLink={{ url: item.url }}
            addonLeft={
                <>
                    {item?.metadata?.batchThumbnails && item?.metadata?.batchThumbnails[0]?.thumbnails[0]?.linkToFile ? (
                        <MailItemThumbnail {...item?.metadata?.batchThumbnails[0]?.thumbnails[0]} />
                    ) : !item.metadata?.cost ? item?.metadata?.contentThumbnails && item.metadata?.contentThumbnails?.thumbnails && item.metadata?.contentThumbnails?.thumbnails[0]?.linkToFile ? (
                        <MailItemThumbnail {...item.metadata?.contentThumbnails?.thumbnails[0]} />
                    ) : item?.metadata?.contentThumbnails && item?.metadata?.contentThumbnails?.topupRequired ? (
                        <MailItemTopUpRequired />
                    ) : item?.metadata?.envelopeThumbnails && item.metadata?.envelopeThumbnails?.thumbnails && item.metadata?.envelopeThumbnails?.thumbnails[0]?.linkToFile ? (
                        <MailItemThumbnail {...item.metadata?.envelopeThumbnails?.thumbnails[0]} />
                    ) : item?.metadata?.envelopeThumbnails && item?.metadata?.envelopeThumbnails?.topupRequired ? (
                        <MailItemTopUpRequired />
                    ) : (
                        <MailItemNoThumbnail mailType={MailPartTypeEnum['Mail-Envelopes']} />
                    ) : null}

                    {item.metadata.cost && (
                        <div className={`list-item-media-container list-item-featured ${ColourSolid.Success}`}>
                            <FontAwesomeIcon icon={faRefresh} fixedWidth={true} />
                        </div>
                    )}
                </>
            }
            title={item.message}
            description={!isMedium ? FormatLocaleRelativeStrict(item.eventDate, currentLocale) : undefined}
            addonRight={isMedium ? { content: FormatLocaleRelativeStrict(item.eventDate, currentLocale) } : undefined}
        />
    )
}

const ActivityFeed = forwardRef((props, ref: any) => {
    const {
        data,
        status,
        error,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage
    } = useInfiniteClientActivity();

    const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 } );

    const { t } = useTranslation();

    const GetItemCount = useCallback(() => {
        let itemsLoaded = 0;
        data?.pages.map((group: any) => itemsLoaded = itemsLoaded + group.data.length);

        return {loaded: itemsLoaded, total: data?.pages[0].totalRecords };
    }, [data]);

    useEffect(() => {
        if(data) {
            setItemCounts(GetItemCount());
        }
    }, [GetItemCount, data]);

    return (
        <>
            <h6 className={'sub-heading mb-xs'}>{t('activityFeed.title')}</h6>
            {status === "loading" ? (
                <ContentLoader rounded={true} childCount={10} childHeight={'6.6em'} />
            ) : status === "error" ? (
                <ContentBlock error={true} title="" content={t('activityFeed.componentError')} />
            ) : (
                <div className={'card overview-activity-container'} style={{ overflow: 'hidden'}}>
                    <div className={'list-group'}>
                        {data.pages.map((group: any, groupIndex: number) => {
                            if(group.totalRecords === 0) {
                                return (
                                    <RichEmptyState
                                        key={`activityItem${groupIndex}Empty`}
                                        title={t('activityFeed.noData.title')}
                                        description={t('activityFeed.noData.description')}
                                        media={<ActivityImage />}
                                    />
                                )
                            }

                            return group.data.map((item: ActivityItemType, index: number) =>
                                <ActivityItem {...item} key={`activityItem${groupIndex}${index}`} />
                            )
                        })}

                        {itemCounts.total > 0 && (
                            <InfiniteLoadTrigger
                                loaded={itemCounts.loaded}
                                totalCount={itemCounts.total}
                                hasNextPage={hasNextPage}
                                fetchNextPage={fetchNextPage}
                                isFetchingNextPage={isFetchingNextPage}
                            />
                        )}
                    </div>
                </div>
            )}
        </>
    )
});

export default ActivityFeed
